import { useSignOut } from "react-firebase-hooks/auth";
import { useDownloadURL } from "react-firebase-hooks/storage";
import { ref } from "firebase/storage";

import { auth, storage } from "@/config/firebaseConfig";
import { NotificationBell } from "@/components/NotificationBell";
import { OrgScopeSwitcher } from "@/components/sidebar/OrgScopeSwitcher";
import { useProductScope } from "@/context/ProductScopeContext";
import { useProfile } from "@/context/ProfileContext";
import { useMemberAvatarUrl } from "@/hooks/use-member-avatar-url";
import { useSidebarNavFeature } from "@/hooks/use-sidebar-nav-features";
import { AnserToggle } from "./AnserToggle";
import { ViewAsSwitch } from "./ViewAsSwitch";
import { ShellSearch } from "./ShellSearch";
import { ShellUserMenu } from "./ShellUserMenu";

/**
 * The 76 px desktop header from the platform sketch: the people search, the
 * Anser panel toggle, the workspace scope switch (the sketch's "Visa som"
 * slot), the notification bell and the account block. The search only
 * exists inside the Coach workspace for members who may see the people
 * list; Recruit and Leads get a spacer. Tokens only.
 */
export function AppTopBar() {
  const { memberProfile, memberRole, businessProfile } = useProfile();
  const { activeProduct } = useProductScope();
  const teamsFeature = useSidebarNavFeature("teams");
  const avatarUrl = useMemberAvatarUrl(memberProfile?.uid);
  const [signOut] = useSignOut(auth);
  const [businessLogoUrl] = useDownloadURL(
    businessProfile?.uid
      ? ref(storage, `businessUsersAvatars/${businessProfile.uid}/profile_image`)
      : undefined,
  );

  if (!memberProfile) return null;

  const canSearchPeople =
    activeProduct === "coach" &&
    teamsFeature?.children?.some((c) => c.url === "/people") === true;

  return (
    <header
      data-testid="app-top-bar"
      className="flex h-[76px] shrink-0 items-center gap-[18px] border-b border-g-line bg-g-bg-shell px-8"
    >
      {canSearchPeople ? (
        <ShellSearch className="max-w-[640px]" />
      ) : (
        <div className="flex-1" aria-hidden="true" />
      )}
      <div className="ml-auto flex shrink-0 items-center gap-3">
        <AnserToggle />
        <ViewAsSwitch />
        <OrgScopeSwitcher
          variant="chip"
          orgName={businessProfile?.business ?? "Graftus"}
          orgLogoUrl={businessLogoUrl}
          isLoading={!businessProfile}
        />
        <NotificationBell className="h-10 w-10 rounded-g-md text-g-ink-600 hover:bg-g-primary-wash hover:text-g-ink-900" />
        <span aria-hidden="true" className="mx-1 h-[30px] w-px bg-g-line-strong" />
        <ShellUserMenu
          user={{
            name: memberProfile.name ?? "",
            email: memberProfile.email ?? "",
            avatar: avatarUrl,
          }}
          role={memberRole}
          onSignOut={() => void signOut()}
        />
      </div>
    </header>
  );
}
